import { debounce } from '@mui/material/utils';
import { useQuery } from '@tanstack/react-query';
import { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'src/components/Autocomplete';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import { Chip } from 'src/components/Chip';
import { ListItem } from 'src/components/ListItem';
import { InputAdornment, TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { useSession } from 'src/contexts/SessionContext';
import { Recipient } from 'src/models/conversation/_types';
import { searchAvailableRecipients } from 'src/models/conversation/conversation';
import { isTrainer } from 'src/models/user/utils';

interface UserInputProps {
  value: Recipient[];
  onChange: (e: SyntheticEvent | undefined, newValue: Recipient[]) => void;
}

export const UserInput: React.FC<UserInputProps> = ({ value, onChange }) => {
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const { user } = useSession();
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });

  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setDebouncedSearchTerm(value), 300),
    []
  );

  const handleInputChange = useCallback(
    (_event: any, newInputValue: string) => {
      setInputValue(newInputValue);
      debouncedSetSearchTerm(newInputValue);
    },
    [debouncedSetSearchTerm]
  );

  const { data: recipients, isLoading: recipientsLoading } = useQuery({
    queryKey: ['available recipients', debouncedSearchTerm],

    queryFn: async () => {
      if (debouncedSearchTerm.length < 1) return [];
      const list = await searchAvailableRecipients({
        search: debouncedSearchTerm,
      });
      return list;
    },

    enabled: debouncedSearchTerm.length > 0,
  });

  const candidates = useMemo(() => {
    return (recipients || []).filter(
      (c) =>
        isTrainer(user) || !c.type || c.type === 'User' || c.type === 'Trainer'
    );
  }, [recipients, user]);

  const noOptionsText =
    inputValue.length < 1
      ? tCommon('messages.charactersNeeded')
      : tCommon('messages.noOptions');

  const handleFocus = () => setIsFocused(true);
  const handleBlur = (event: React.FocusEvent) => {
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (relatedTarget?.closest('.MuiChip-root')) {
      return;
    }
    setIsFocused(false);
  };

  const recipientString = useMemo(() => {
    if (value && value.length > 0) {
      if (value.length <= 3) {
        return value.map((r) => r.nickname).join(', ');
      } else {
        const visibleRecipients = value
          .slice(0, 3)
          .map((r) => r.nickname)
          .join(', ');
        return `${visibleRecipients} ${tCommon('pagination.andMore', {
          count: value.length - 3,
        })}`;
      }
    }
  }, [value, tCommon]);

  return (
    <Box mx={1}>
      <Autocomplete
        multiple
        disablePortal
        id="combo-box-demo"
        options={candidates}
        loading={recipientsLoading}
        onChange={onChange}
        value={value}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        getOptionLabel={(option) => option.nickname}
        renderOption={(props, option) => {
          return (
            <ListItem
              {...(props as any)}
              primary={
                <span dangerouslySetInnerHTML={{ __html: option.nickname }} />
              }
              secondary={
                option.aliases?.length ? option.aliases.join(', ') : ''
              }
            />
          );
        }}
        noOptionsText={noOptionsText}
        filterOptions={(x) => x}
        popupIcon={false}
        forcePopupIcon={false}
        sx={{
          width: '100%',
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder=""
            variant="standard"
            onFocus={handleFocus}
            onBlur={handleBlur}
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              startAdornment: (
                <>
                  <InputAdornment position="start" sx={{ mr: 1 }}>
                    {tCommon('labels.to')}
                  </InputAdornment>
                  {isFocused ? (
                    params.InputProps.startAdornment
                  ) : (
                    <Typography component="span" sx={{ mr: 1 }}>
                      {recipientString}
                    </Typography>
                  )}
                </>
              ),
            }}
          />
        )}
        renderTags={(tagValue, getTagProps) =>
          isFocused
            ? tagValue.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={
                    <span
                      dangerouslySetInnerHTML={{ __html: option.nickname }}
                    />
                  }
                  avatar={
                    option.profileImage ? (
                      <Avatar
                        size="xsmall"
                        src={option.profileImage}
                        sx={{ ml: 0.5 }}
                      />
                    ) : (
                      <Avatar size="xsmall" sx={{ ml: 0.5 }}>
                        {option.nickname[0].toUpperCase()}
                      </Avatar>
                    )
                  }
                  {...getTagProps({ index })}
                  key={option.id}
                />
              ))
            : null
        }
      />
    </Box>
  );
};
